import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceArea,
} from 'recharts';
import { dpe } from '@/styles/theme';

// ── DPE histogram (A → G, passoires F+G highlighted) ─────────────────────────

const DPE_ORDER = ['A', 'B', 'C', 'D', 'E', 'F', 'G'] as const;

const TOOLTIP_STYLE = {
  borderRadius: 10,
  border: '1px solid #e4e8f0',
  fontSize: 12,
  boxShadow: '0 8px 24px -16px rgba(17,25,58,.18)',
} as const;

export function DpeHistogram({
  distribution,
  ingesting = false,
}: {
  distribution: Record<string, number>;
  ingesting?: boolean;
}) {
  const total = Object.values(distribution).reduce((sum, n) => sum + n, 0);
  const data = DPE_ORDER.map((label) => ({ label, count: distribution[label] ?? 0 }));
  const passoires = (distribution.F ?? 0) + (distribution.G ?? 0);
  const passoiresPct = total > 0 ? Math.round((passoires / total) * 100) : 0;

  return (
    <div className="rounded-panel border border-line bg-white p-4 shadow-card">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          <h3 className="font-display text-[14px] font-bold text-ink">
            Performance énergétique (DPE)
          </h3>
          <p className="text-[11.5px] text-faint">Répartition du parc bâti par étiquette</p>
        </div>
        <span
          className="flex-shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold"
          style={{ background: '#fdecec', color: '#e30613' }}
          aria-label={`${passoiresPct} pour cent de passoires énergétiques`}
        >
          {passoiresPct}% passoires
        </span>
      </div>

      {total === 0 ? (
        <p className="py-10 text-center text-sm text-muted">
          {ingesting ? (
            <span className="animate-pulse">Calcul des diagnostics DPE en cours…</span>
          ) : (
            'Aucune donnée DPE disponible'
          )}
        </p>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
              <ReferenceArea x1="F" x2="G" fill="#e30613" fillOpacity={0.07} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 12, fontWeight: 600, fill: '#41496a' }}
                axisLine={{ stroke: '#e4e8f0' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#8b93a9' }}
                axisLine={false}
                tickLine={false}
                width={34}
                allowDecimals={false}
              />
              <Tooltip
                cursor={{ fill: 'rgba(17,25,58,0.04)' }}
                contentStyle={TOOLTIP_STYLE}
                formatter={(value) => [Number(value).toLocaleString('fr-FR'), 'Bâtiments']}
              />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={46}>
                {data.map((d) => (
                  <Cell key={d.label} fill={dpe[d.label]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-2 flex items-center justify-end gap-1.5 text-[11px] text-faint">
            <span
              className="inline-block h-2.5 w-2.5 rounded-sm"
              style={{ background: '#e30613', opacity: 0.18 }}
              aria-hidden="true"
            />
            Zone passoires (F + G)
          </div>
        </>
      )}
    </div>
  );
}

// ── Occupation donut (buildings by nature) ───────────────────────────────────

const NATURE_PALETTE = ['#1b2559', '#2c3a78', '#2563eb', '#5cbd92', '#cf9d4a', '#8a93a8'];

export function OccupationDonut({
  data,
  ingesting = false,
}: {
  data: Record<string, number>;
  ingesting?: boolean;
}) {
  const entries = Object.entries(data)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 6);
  const total = entries.reduce((sum, [, n]) => sum + n, 0);
  const chart = entries.map(([nature, count], i) => ({
    nature,
    count,
    color: NATURE_PALETTE[i % NATURE_PALETTE.length],
  }));

  return (
    <div className="rounded-panel border border-line bg-white p-4 shadow-card">
      <h3 className="font-display text-[14px] font-bold text-ink">Occupation du bâti</h3>
      <p className="mb-2 text-[11.5px] text-faint">Bâtiments par nature</p>

      {total === 0 ? (
        <p className="py-10 text-center text-sm text-muted">
          {ingesting ? (
            <span className="animate-pulse">Calcul de l&apos;occupation du bâti en cours…</span>
          ) : (
            'Aucune donnée disponible'
          )}
        </p>
      ) : (
        <div className="flex items-center gap-3">
          <ResponsiveContainer width="48%" height={150}>
            <PieChart>
              <Pie
                data={chart}
                dataKey="count"
                nameKey="nature"
                cx="50%"
                cy="50%"
                innerRadius={42}
                outerRadius={64}
                paddingAngle={2}
                stroke="none"
              >
                {chart.map((d) => (
                  <Cell key={d.nature} fill={d.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                formatter={(value) => Number(value).toLocaleString('fr-FR')}
              />
            </PieChart>
          </ResponsiveContainer>

          <ul className="flex-1 space-y-1.5">
            {chart.map((d) => (
              <li key={d.nature} className="flex items-center gap-2 text-[12px]">
                <span
                  className="h-2.5 w-2.5 flex-shrink-0 rounded-sm"
                  style={{ background: d.color }}
                  aria-hidden="true"
                />
                <span className="flex-1 truncate text-body" title={d.nature}>
                  {d.nature}
                </span>
                <span className="font-mono text-muted">
                  {Math.round((d.count / total) * 100)}%
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
